<script setup lang="ts">
import { computed, ref } from 'vue';
import AppIcon from '@/components/App/Icon/AppIcon.vue';
import { TournamentTheme } from '@/types/tournaments';
import type { BaseSelectOption } from '@/types/base.types';

type BaseSelectTheme = 'main' | 'tournament' | 'header' | 'secondary-ufc' | TournamentTheme;

const props = withDefaults(defineProps<{
  options: BaseSelectOption[];
  selectedOption: BaseSelectOption;
  showSelectedOptionsInOptions?: boolean;
  theme?: BaseSelectTheme;
}>(), {
  theme: 'main',
});

const emits = defineEmits(['update:selectedOption', 'select']);

const currentOption = computed({
  get() {
    return props.selectedOption;
  },
  set(newValue) {
    emits('update:selectedOption', newValue);
  },
});

const optionsList = computed(() => {
  if (props.showSelectedOptionsInOptions) {
    return props.options;
  }

  return props.options.filter((option) => option.slug !== currentOption.value.slug);
});

const isOpened = ref(false);

const toggleDropdown = () => {
  isOpened.value = !isOpened.value;
};

const selectOption = (payload: BaseSelectOption) => {
  currentOption.value = payload;
  emits('select', payload);
  isOpened.value = false;
};
</script>

<template>
  <div class="base-select">
    <button
      class="base-select__button title-16-light"
      :class="{
        'base-select__button--main': props.theme === 'main',
        'base-select__button--tournament': props.theme === 'tournament',
        'base-select__button--secondary-ufc': props.theme === 'secondary-ufc',
        'base-select__button--pos': props.theme === TournamentTheme.PathOfSage,
        'base-select__button--fe': props.theme === TournamentTheme.FootballElements,
        'base-select__button--ufc': props.theme === TournamentTheme.UFC,
        'base-select__button--header': props.theme === 'header',
        'is-opened': isOpened,
      }"
      @click="toggleDropdown"
    >
      <AppIcon
        v-if="props.theme === TournamentTheme.FootballElements"
        name="ball"
        class="base-select__left-icon"
      />

      <AppIcon
        v-if="currentOption.icon"
        :key="currentOption.icon"
        :name="currentOption.icon"
        class="base-select__left-icon--header"
        :class="{
          'base-select__left-icon--opened': props.theme === 'header' && isOpened
        }"
      />

      {{ currentOption.label }}

      <AppIcon
        name="arrow-down"
        class="base-select__toggle-icon"
        :class="{'base-select__toggle-icon--opened': isOpened}"
      />
    </button>

    <transition name="base-select">
      <ul
        v-if="isOpened"
        class="base-select__list"
        :class="{
          'base-select__list--main': props.theme === 'main',
          'base-select__list--tournament': props.theme === 'tournament',
          'base-select__list--secondary-ufc': props.theme === 'secondary-ufc',
          'base-select__list--pos': props.theme === TournamentTheme.PathOfSage,
          'base-select__list--fe': props.theme === TournamentTheme.FootballElements,
          'base-select__list--ufc': props.theme === TournamentTheme.UFC,
          'base-select__list--header': props.theme === 'header',
        }"
      >
        <li
          v-for="(option, index) in optionsList"
          :key="option.slug"
        >
          <button
            :tabindex="index + 1"
            class="base-select__item title-16-light"
            :class="{
              'base-select__item--main': props.theme === 'main',
              'base-select__item--tournament': props.theme === 'tournament',
              'base-select__item--secondary-ufc': props.theme === 'secondary-ufc',
              'base-select__item--pos': props.theme === TournamentTheme.PathOfSage,
              'base-select__item--fe': props.theme === TournamentTheme.FootballElements,
              'base-select__item--ufc': props.theme === TournamentTheme.UFC,
              'base-select__item--header': props.theme === 'header',
              'current': currentOption.slug === option.slug,
            }"
            @click="selectOption(option)"
          >
            <AppIcon
              v-if="option.icon"
              :name="option.icon"
              class="base-select__item-icon"
            />
            {{ option.label }}
          </button>
        </li>
      </ul>
    </transition>
  </div>
</template>

<style lang="scss" scoped>
.base-select {
  position: relative;
}

.base-select__button {
  position: relative;
  width: 100%;
  min-height: 48px;
  padding: 8px 40px;
  background-color: $clear-10;
  border-radius: 8px;
  -webkit-backdrop-filter: blur(30px);
  backdrop-filter: blur(30px);

  &--main {
    background: $blue-5;
    text-align: left;
    padding-left: 12px;
    color: $clear-80;

    &::before {
      content: "";
      position: absolute;
      inset: 1px;
      border-radius: 8px;
      padding: 1px;
      background: $gradient-20;
      mask: linear-gradient($clear-100 0%, $clear-100 0%) content-box,
      linear-gradient($clear-100 0%, $clear-100 0%);
      mask-composite: exclude;
      pointer-events: none;
      transition: all 0.2s;
    }
  }

  &--pos {
    background-color: $lilac-150;
    text-align: left;
    padding-left: 20px;
  }

  &--fe {
    background-color: $blue-5;
    text-align: left;
    color: $green;
  }

  &--ufc {
    border-radius: 2px;
    background-color: $ufc-dark-20;
    border: 1px solid $ufc-primary-10;

    .base-select__toggle-icon {
      color: $ufc-primary-100;
    }
  }

  &--secondary-ufc {
    padding-left: 12px;
    text-align: left;
    border-radius: 0;
    background-color: transparent;
    border: 1px solid $ufc-primary-40;
    transition: $transition-base;
    font-family: Gilroy, sans-serif;

    .base-select__toggle-icon {
      color: $ufc-primary-100;
    }

    &:hover,
    &:focus {
      background-color: $ufc-primary-20;
      border: 1px solid $ufc-primary-40;
    }
  }

  &--header {
    padding: 8px 32px 8px 8px;
    border-radius: 16px;
    font-size: 12px;
    min-height: auto;
    color: $success-100;
    display: flex;
    gap: 8px;
    align-items: center;
    -webkit-backdrop-filter: initial;
    backdrop-filter: initial;
    background-color: #0B324B;
    border: 1px solid $header-border-color;

    &.is-opened {
      border-radius: 16px 16px 0 0;
      border-bottom-color: transparent;
    }
  }
}

.base-select__left-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  width: 17px;
  height: 17px;
  transform: translateY(-50%);

  &--header {
    color: $success-100;
    width: 16px;
    height: 16px;
  }
}

.base-select__toggle-icon {
  position: absolute;
  right: 8px;
  top: 50%;
  width: 32px;
  height: 32px;
  transform: translateY(-50%);
  color: $clear-70;

  &--opened {
    transform: translateY(-50%) rotate(180deg);
  }
}

.base-select__list {
  z-index: $z-level-2;
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  background-color: $clear-10;
  border-radius: 8px;
  overflow: hidden;
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);

  &--main {
    background-color: $main-surface-color;
    padding: 4px;
  }

  &--pos {
    background-color: $lilac-150;
  }

  &--ufc {
    border-radius: 2px;
    background-color: $ufc-dark-20;
    border: 1px solid $ufc-primary-10;
  }

  &--secondary-ufc {
    border-radius: 0;
    background-color: $ufc-primary-10;
    border: 1px solid $ufc-primary-40;
  }

  &--header {
    top: 100%;
    border-radius: 0 0 16px 16px;
    backdrop-filter: blur(60px);
    background-color: #12314a;
    border: 1px solid $header-border-color;
    border-top-color: transparent;
  }
}

.base-select__item {
  width: 100%;
  padding: 16px 40px;
  text-align: center;
  cursor: pointer;
  color: $clear-80;
  transition: all 250ms ease-in-out;

  &:hover,
  &:focus {
    background-color: $clear-100;
    color: $lilac-150;
  }

  &-icon {
    width: 16px;
    height: 16px;
  }

  &--main {
    padding-top: 12px;
    padding-bottom: 12px;
    padding-left: 12px;
    border-radius: 4px;
    text-align: left;

    &:hover,
    &:focus {
      background: $blue-10;
      color: $clear-80;
    }
  }

  &--main.current {
    background: $gradient-20;
    color: $green;
  }

  &--pos {
    text-align: left;
    padding-left: 20px;
    color: $clear-60;

    &:hover,
    &:focus {
      color: $clear-100;
      background-color: transparent;
    }
  }

  &--fe {
    text-align: left;
    color: $clear-60;
  }

  &--ufc {
    text-align: center;
    color: $clear-60;

    &:hover,
    &:focus {
      color: $clear-100;
      background-color: transparent;
    }
  }

  &--secondary-ufc {
    padding-left: 12px;
    padding-right: 12px;
    text-align: left;
    color: $clear-60;
    font-family: Gilroy, sans-serif;

    &:hover,
    &:focus {
      color: $clear-100;
      background-color: transparent;
    }
  }

  &--secondary-ufc.current {
    color: $ufc-primary-100;
  }

  &--header {
    padding: 8px;
    font-size: 12px;
    text-align: left;
    display: flex;
    gap: 8px;

    &:hover,
    &:focus {
      color: $success-100;
      background-color: transparent;
    }
  }
}

.base-select-enter-active,
.base-select-leave-active {
  transition: opacity 300ms ease;
}

.base-select-enter,
.base-select-leave-to {
  opacity: 0;
}
</style>
